import React from 'react';
import Seo from '../../components/Seo/Seo';

const TermsOfService = () => {
  return (
    <div>
      <Seo
        title="Terms of Service"
        description="Review the terms and conditions for booking and using The Skilled Group's pest control and cleaning services."
        canonicalPath="/terms-of-service"
      />
      {/* Page Header */}
      <div className="page-header">
        <div className="container-custom">
          <h1>Terms and Conditions</h1>
          <p>The Skilled Group</p>
        </div>
      </div>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto prose prose-lg max-w-none">
            {/* Important Notice */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                Terms and Conditions - The Skilled Group
              </h2>
              <p className="text-gray-600 leading-relaxed">
                <strong>IMPORTANT:</strong> By booking or using our services, you agree to be
                bound by these Terms and Conditions. The Skilled Group reserves the right to update or
                modify these terms at any time without prior notice. Updated versions will be
                published on our website. Please review periodically.
              </p>
            </div>

            {/* Business Details */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                Business Details
              </h2>
              <p className="text-gray-700 font-semibold mb-2">The Skilled Group</p>
              <p className="text-gray-600 text-sm">
                Email:{' '}
                <a href="mailto:info@theskilledgroup.com" className="text-primary-500 hover:text-primary-600">
                  info@theskilledgroup.com
                </a>
              </p>
              <p className="text-gray-600 text-sm">Phone: 0466708090</p>
            </div>

            {/* Definitions */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                1. Definitions
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li><strong>Client / You</strong> - The person or entity booking our services.</li>
                <li><strong>We / Us / The Skilled Group</strong> - The Skilled Group and its contractors.</li>
                <li><strong>Services</strong> - Pest Control, Carpet Steam Cleaning, Mattress Cleaning, Tile and Grout Cleaning, Couch Steam Cleaning.</li>
                <li><strong>Technician</strong> - The person performing the service.</li>
                <li><strong>Property</strong> - The premises where services are delivered.</li>
                <li><strong>Heavily Soiled</strong> - Conditions requiring significantly more time, effort, or chemicals than standard service.</li>
                <li><strong>Best Efforts</strong> - Reasonable professional judgment based on available conditions.</li>
              </ul>
            </div>

            {/* General Terms */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                2. General Terms
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>These Terms apply to all bookings and override any conflicting client communication.</li>
                <li>Abuse, harassment, or threatening behavior toward our staff will result in immediate termination of services without refund.</li>
                <li>Service inclusions and exclusions are available on our website.</li>
                <li>We are not liable for delays caused by events beyond our control.</li>
                <li>These Terms are governed by applicable laws.</li>
              </ul>
            </div>

            {/* Bookings */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                3. Bookings and Services
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Bookings are made via our website or approved channels.</li>
                <li>A booking is confirmed once we issue confirmation (email/SMS).</li>
                <li>Each booking is treated as a single service instance.</li>
                <li>Clients must ensure correct service selection.</li>
                <li>Additional requirements not disclosed at booking may incur extra charges.</li>
              </ul>
            </div>

            {/* Property Access */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                4. Property Access
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Clients must ensure access at the scheduled time. Failure to provide access may result in:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>$50/hour non-access fee OR up to 100% cancellation fee</li>
              </ul>

              <p className="text-gray-600 leading-relaxed mt-4 mb-3">
                Additional conditions:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Utilities (electricity and water) must be available.</li>
                <li>Work areas must be accessible and reasonably prepared.</li>
                <li>Personal belongings must be removed from service areas.</li>
                <li>We may take photos for documentation and quality assurance.</li>
              </ul>
            </div>

            {/* Pricing */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                5. Pricing and Quotes
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                All quotes are estimates based on standard conditions. Price may change due to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Incorrect details</li>
                <li>Heavily soiled conditions</li>
                <li>Additional areas/items</li>
                <li>Access or parking issues</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                Approval of revised pricing authorizes us to proceed and charge accordingly.
              </p>
            </div>

            {/* Payment */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                6. Payment Terms
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>A pre-authorisation may be required.</li>
                <li>Final payment is processed upon completion.</li>
                <li>Approved extras are chargeable (verbal or written approval).</li>
                <li>Payment cannot be withheld due to dissatisfaction - complaints must follow our process.</li>
              </ul>

              <h3 className="text-xl font-heading font-semibold text-gray-800 mt-6 mb-3">
                6.1 Failed Payments
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Must be resolved within 24 hours</li>
                <li>$25 admin fee applies</li>
                <li>Interest may apply on overdue balances</li>
                <li>Unpaid accounts may be sent to collections</li>
              </ul>

              <h3 className="text-xl font-heading font-semibold text-gray-800 mt-6 mb-3">
                6.2 Chargebacks
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Clients must follow complaint procedures first</li>
                <li>Invalid chargebacks may incur additional fees</li>
                <li>We reserve the right to recover unpaid amounts</li>
              </ul>
            </div>

            {/* Cancellations */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                7. Cancellations and Rescheduling
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>More than 24 hours notice: Free</li>
                <li>Less than 24 hours: $90 fee</li>
                <li>Same-day cancellation: Up to 100% charge</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4 mb-3">
                Charges also apply if:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Property inaccessible</li>
                <li>Conditions misrepresented</li>
                <li>Required upgrades refused</li>
              </ul>
            </div>

            {/* Limitations */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                8. Service Limitations & Risks
              </h2>
              <p className="text-gray-600 leading-relaxed mb-3">
                Due to the nature of cleaning and pest control services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Results may vary depending on condition, usage, and material type</li>
                <li>We do not guarantee removal of all stains, odours, or contamination</li>
                <li>Some stains, damage, or wear may be permanent</li>
                <li>Steam cleaning may not fully restore heavily worn materials</li>
              </ul>

              <h3 className="text-xl font-heading font-semibold text-gray-800 mt-6 mb-3">
                Tile & Grout Specific:
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Results depend on grout condition and prior maintenance</li>
                <li>We are not responsible for failure of aged grout</li>
                <li>We are not responsible for loose or damaged tiles</li>
                <li>We are not responsible for hidden structural issues</li>
              </ul>
            </div>

            {/* Pest Control Terms */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                9. Pest Control Terms
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Pest control treatments are limited to the pests and areas specified in your booking.
              </p>

              <p className="text-gray-600 leading-relaxed mb-3">Important conditions:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Results depend on hygiene, building condition, neighbouring infestations, and environmental factors</li>
                <li>No guarantee of permanent eradication is provided</li>
                <li>Some pests (including termites, rodents, German cockroaches, and bedbugs) may require specialised or ongoing treatments</li>
                <li>Structural repairs, pest-proofing, or building works are not included unless agreed in writing</li>
                <li>Clients must follow all pre-treatment and after-care instructions</li>
                <li>Failure to follow instructions may void any complimentary re-treatment (if offered)</li>
                <li>Clients must follow all safety advice, including vacating premises where required and protecting pets</li>
                <li>Services are performed by licensed technicians only</li>
              </ul>
            </div>

            {/* Service Quality */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                10. Service Quality & Re-Service Policy
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Issues must be reported within 24 hours</li>
                <li>We may offer a re-service where appropriate</li>
              </ul>

              <p className="text-gray-600 leading-relaxed mt-4 mb-3">Re-service does not apply if:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Conditions were misrepresented</li>
                <li>Instructions were not followed</li>
                <li>Area has been re-used or altered</li>
              </ul>

              <p className="text-gray-600 leading-relaxed mt-4">
                Re-service is the only remedy offered - not refunds.
              </p>
            </div>

            {/* Liability */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                11. Liability and Claims
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Claims must be reported within 24 hours</li>
                <li>Clients must allow us to rectify issues before third-party involvement</li>
              </ul>

              <p className="text-gray-600 leading-relaxed mt-4 mb-3">We are not responsible for:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Pre-existing damage</li>
                <li>Wear and tear</li>
                <li>Undisclosed hazards</li>
              </ul>

              <p className="text-gray-600 leading-relaxed mt-4 mb-3">We are not liable for:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Indirect or consequential losses</li>
              </ul>
            </div>

            {/* Complaints */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                12. Complaints Procedure
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                All complaints must be submitted within 24 hours to:{' '}
                <a href="mailto:info@theskilledgroup.com" className="text-primary-500 hover:text-primary-600">
                  info@theskilledgroup.com
                </a>
              </p>

              <p className="text-gray-600 leading-relaxed mb-3">Include:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Name and contact details</li>
                <li>Description of issue</li>
                <li>Supporting evidence</li>
                <li>Requested resolution</li>
              </ul>

              <p className="text-gray-600 leading-relaxed mt-4">
                Failure to follow this process may result in dismissal.
              </p>
            </div>

            {/* Content Protection */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                13. Website and Content Protection
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                All content, processes, pricing, and service structures are the intellectual property of The Skilled Group. Unauthorized use is prohibited.
              </p>

              <p className="text-gray-600 leading-relaxed mb-3">We reserve the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Take legal action</li>
                <li>Claim damages</li>
                <li>Recover legal costs</li>
              </ul>
            </div>

            {/* Force Majeure */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                14. Force Majeure
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We are not responsible for delays or failure due to events beyond our control, including natural disasters or emergencies.
              </p>
            </div>

            {/* Acceptance */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                15. Acceptance
              </h2>
              <p className="text-gray-600 leading-relaxed mt-4">
                By booking our services, you confirm that you have read, understood, and agreed to these Terms and Conditions.
              </p>
            </div>

            {/* Contact */}
            <div className="mb-4">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                Contact Us
              </h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <p className="text-gray-700 font-semibold mb-2">The Skilled Group</p>
                <p className="text-gray-600 text-sm">
                  Email:{' '}
                  <a href="mailto:info@theskilledgroup.com" className="text-primary-500 hover:text-primary-600">
                    info@theskilledgroup.com
                  </a>
                </p>
                <p className="text-gray-600 text-sm">Phone: 0466708090</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;